import React, { useState } from "react";
import { Container, Row, Col, Button, Spinner } from "reactstrap";
import { useNavigate } from "react-router-dom";
import styles from "../../../../modules/Library/AdverseEvent/css/adverseEvent.module.scss";
import ip from "../../IPinventory/css/ipInventory.module.scss";
import { TRANSFER_PATHS } from "../../../../Routes/paths";
import {
  formatDate,
  GetUserData,
  showToastMessage,
} from "../../../../common/service/CommonService";
import sf from "../../../../assets/scss/common/searchFilter.module.scss";
import CommonTableIndexWise from "../../../../Components/Common/CommonTableIndexWise";
import { useTransferService } from "../api/useTransferService";
import { queryClient } from "../../../../api/queryClient";
import DeleteModal from "../../../../Components/Common/DeleteModal";
import {
  PERMISSION_ACTIONS,
  PERMISSION_MODULES,
} from "../../../../common/constants/PermissionConstants";
import CommonButton from "../../../../Components/Common/CommonButton";
import PinVerificationModal from "../../DailyReconsilation/components/PinVerificationModal";
import StatCard from "../../../../Components/Common/StatCard";
import FilePreviewModal from "../../../Study/Attachments/components/FilePreviewModal";
import { useGlobalStudy } from "context/StudyContext";
import { useAuditService } from "modules/Audit/api/useAuditQuery";
import NoStudySelectedPlaceholder from "../../../../Components/Common/NoStudySelectedPlaceholder";

interface TransferRecord {
  transferid: number;
  transfernumber: string;
  transferfrom: string;
  transferto: string;
  productname: string;
  batchlotno: string;
  strength: string;
  dosageform: string;
  transferquantity: number;
  createddate: string;
  status: string;
  isverified: boolean;
  total_count: number;
  verifiedbyname?: string;
  transfertypetext?: string;
  pending_verification?: number;
  completed_verification?: number;
}

const TransferList: React.FC = () => {
  document.title = "Drug Transfer Log | ZENTIXS ORBIT";
  const navigate = useNavigate();
  const userData = GetUserData();
  const [searchText, setSearchText] = useState<string>("");
  const [currentPageNumber, setcurrentPageNumber] = useState<number>(1);
  const [searchFocused, setSearchFocused] = useState(false);

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [verifyModal, setVerifyModal] = useState<boolean>(false);
  const [selectedRecord, setSelectedRecord] = useState<TransferRecord | null>(
    null,
  );
  const [showDeleteReason, setShowDeleteReason] = useState<boolean>(false);

  const { selectedStudy: globalStudy } = useGlobalStudy();


  const { SelectAllQuery, DeleteMutation, verifyPinMutation } = useTransferService({
    searchText,
    pageNumber: currentPageNumber,
    pageSize: 5,
    studyid: globalStudy?.studyid,
    enableSelectall: true,
  });

  const queryResponse = SelectAllQuery.data as any;
  const data: TransferRecord[] = queryResponse?.data ?? [];

  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pendingVerification, setPendingVerification] = useState<number>(0);
  const [completedVerification, setCompletedVerification] = useState<number>(0);

  //Audit
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const { SelectAllMutation: AuditMutation } = useAuditService();

  const onAuditClick = () => {
    const payload = {
      modulename: "Transfer",
      studyid: globalStudy?.studyid
    };

    AuditMutation.mutate(payload, {
      onSuccess: (data: any) => {
        if (data instanceof Blob) {
          const pdfFile = new File([data], "Transfer_Audit_Log.pdf", {
            type: "application/pdf",
          });
          setPreviewFile(pdfFile);
          setIsPreviewOpen(true);
        }
      },
      onError: (error: any) => {
        showToastMessage("error", "Error fetching audit logs");
        console.error("Error fetching audit logs:", error);
      },
    });
  };

  React.useEffect(() => {
    if (queryResponse) {
      setTotalRecords(data[0]?.total_count || 0);
      setPendingVerification(data[0]?.pending_verification || 0);
      setCompletedVerification(data[0]?.completed_verification || 0);
    }
  }, [queryResponse]);

  const onSearch = (value: string) => {
    setSearchText(value);
    setcurrentPageNumber(1);
  };

  const handleDeleteClick = (item: TransferRecord) => {
    setSelectedRecord(item);
    setDeleteModal(true);
  };

  const handleDeleteWithReason = (payload: {
    id: number | null;
    reason: string;
  }) => {
    if (!selectedRecord) return;
    DeleteMutation.mutate(
      { transferid: selectedRecord.transferid, updatereason: payload.reason },
      {
        onSuccess: (response: any) => {
          showToastMessage("success", response.message);
          queryClient.invalidateQueries({ queryKey: ["transfer-selectAll"] });
          queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
          setDeleteModal(false);
          setSelectedRecord(null);
        },
        onError: (error: any) => {
          showToastMessage("error", error?.message ?? error);
        },
      },
    );
  };

  const handleVerifyClick = (item: TransferRecord) => {
    setSelectedRecord(item);
    setVerifyModal(true);
  };

  const handlePinVerify = (password: string) => {
    const payload = {
      verifypin: password,
      transferid: Number(selectedRecord?.transferid),
      studyid: 0,
      verifiedby: Number(userData?.userid),
    };

    verifyPinMutation.mutate(payload, {
      onSuccess: (response: any) => {
        queryClient.invalidateQueries({ queryKey: ["transfer-selectAll"] });
        queryClient.invalidateQueries({ queryKey: ["verification-notification-count"] });
        showToastMessage("success", response?.message);
        setVerifyModal(false);
        setSelectedRecord(null);
      },
      onError: (error: Error) => {
        showToastMessage("error", error?.message ?? error);
      },
    });
  };

  const columns: any = [
    {
      key: "transfernumber",
      label: "TRANSFER ID",
      type: "link",
      onClick: (row: any) =>
        navigate(TRANSFER_PATHS.VIEW.replace(":id", row.transferid.toString())),
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.link}>{row.transfernumber || "-"}</p>
            <p className={ip.cellSecondary}>
              Sponsor : {row.sponsorname || "-"}
            </p>
          </>
        );
      },
    },
    {
      key: "transferfrom",
      label: "FROM \u2192 TO",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>
              {row.transferfrom || "-"} → {row.transferto || "-"}
            </p>
            <p className={ip.cellSecondary}>{row.transfertypetext || "-"}</p>
          </>
        );
      },
    },
    {
      key: "studycode",
      label: "Study",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.studycode || "-"}</p>
            <p className={ip.cellSecondary}>{row.ipcategory || "-"}</p>
          </>
        );
      },
    },
    {
      key: "productname",
      label: "PRODUCT DETAILS",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.productname || "-"}</p>
            <p className={ip.cellSecondary}>
              {row.batchlotno || "-"} | {row.strength || "-"} |{" "}
              {row.dosageform || ""}
            </p>
          </>
        );
      },
    },
    {
      key: "transferquantity",
      label: "Transfer Quantity",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.transferquantity || "0"}</p>
            <p className={ip.cellSecondary}>
              Out of {row.totalquantity} {row.quantitytype}
            </p>
          </>
        );
      },
    },
    {
      key: "verifiedby",
      label: "VERIFIED BY",
      render: (_: any, row?: any) => {
        if (!row) return null;
        return (
          <>
            <p className={ip.cellPrimary}>{row.verifiedbyname || "-"}</p>
            <p className={ip.cellSecondary}>{formatDate(row.verifieddate)}</p>
          </>
        );
      },
    },
  ];

  const getRowActions = (row: TransferRecord) => ({
    onEdit: !row.isverified
      ? (r: any) =>
        navigate(TRANSFER_PATHS.EDIT.replace(":id", r.transferid.toString()))
      : undefined,
    onDelete: !row.isverified ? (r: any) => handleDeleteClick(r) : undefined,
    onVerify: !row.isverified ? (r: any) => handleVerifyClick(r) : undefined,
  });

  return (
    <div className={styles.dashboardContainer}>
      <Container fluid>
        {!globalStudy?.studyid ? (
          <div >
            <NoStudySelectedPlaceholder moduleName="transfers" />
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mt-4 mb-3 px-2">
              <div>
                <h5 className={`mb-0 ${styles.pageHeaderTitle}`}>
                  Drug Transfer Log
                </h5>
                <p className={styles.pageHeaderSubtitle}>
                  Track study-to-study, site-to-site, and lab transfers.
                </p>
              </div>
              <div className="d-flex gap-2 align-items-center">
                <CommonButton
                  module={PERMISSION_MODULES.DRUG_TRANSFER}
                  action={PERMISSION_ACTIONS.AUDIT}
                  color="primary"
                  onClick={onAuditClick}
                  disabled={AuditMutation.isPending}
                  outline
                  size="sm"
                  className="fw-600 "
                >
                  {AuditMutation.isPending ? (
                    <Spinner size="sm" className="me-2" />
                  ) : (
                    <i className="ri-history-line me-1"></i>
                  )}
                  Audit
                </CommonButton>
                <CommonButton
                  color="primary"
                  className="px-2 fw-600"
                  module={PERMISSION_MODULES.DRUG_TRANSFER}
                  action={PERMISSION_ACTIONS.ADD}
                  onClick={() => navigate(TRANSFER_PATHS.ADD)}
                  size="sm"
                  outline
                >
                  <i className="ri-add-line me-1" /> Initiate Transfer
                </CommonButton>
              </div>
            </div>

            {/* ── Stats & Table Card ── */}
            <Row className="g-3 mb-3 px-1">
              <Col md={4}>
                <StatCard
                  title="TOTAL TRANSFERS"
                  value={totalRecords}
                  subtext="Transfers tracked"
                  icon={<i className="ri-arrow-left-right-line" />}
                  bgColor="#eff6ff"
                  iconColor="#2563eb"
                  trendType="info"
                  subtextIcon="ri-stack-line"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="PENDING VERIFICATION"
                  value={pendingVerification}
                  subtext="Awaiting verification"
                  icon={<i className="ri-time-line" />}
                  bgColor="#fffbeb"
                  iconColor="#d97706"
                  trendType="negative"
                />
              </Col>

              <Col md={4}>
                <StatCard
                  title="COMPLETED VERIFICATION"
                  value={completedVerification}
                  subtext="Verified transfers"
                  icon={<i className="ri-verified-badge-line" />}
                  bgColor="#ecfdf5"
                  iconColor="#059669"
                  trendType="success"
                  subtextIcon="ri-check-double-line"
                />
              </Col>
            </Row>

            <div className={styles.listPanel}>
              <div className={styles.panelHeader}>
                <h2>
                  <i className="ri-exchange-box-line me-2 text-primary fs-18" />
                  Transfer Records
                </h2>
                <div
                  className={`${sf.searchBox} ${searchFocused ? sf.focused : ""}`}
                >
                  <i className={`ri-search-line ${sf.searchIcon}`} />
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchText}
                    className={sf.searchInput}
                    onChange={(e) => onSearch(e.target.value)}
                    onFocus={() => setSearchFocused(true)}
                    onBlur={() => setSearchFocused(false)}
                  />
                </div>
              </div>

              <div className="px-4">
                <CommonTableIndexWise
                  moduleName={PERMISSION_MODULES.DRUG_TRANSFER}
                  columns={columns}
                  data={data}
                  loading={SelectAllQuery.isLoading || SelectAllQuery.isFetching}
                  pageSize={5}
                  currentPage={currentPageNumber}
                  totalRecords={data[0]?.total_count || 0}
                  onPageChange={setcurrentPageNumber}
                  actions={getRowActions}
                />
              </div>
            </div>
          </>
        )}

        {/* File Preview Modal */}
        {isPreviewOpen && (
          <FilePreviewModal
            file={previewFile}
            isOpen={isPreviewOpen}
            size={"xl"}
            onClose={() => {
              setIsPreviewOpen(false);
              setPreviewFile(null);
            }}
          />
        )}

        {deleteModal && (
          <DeleteModal
            isopen={deleteModal}
            recordId={selectedRecord?.transferid}
            recordName={`${selectedRecord?.transfernumber}`}
            toggle={() => setDeleteModal(false)}
            onDeleteClick={handleDeleteWithReason}
            isLoading={DeleteMutation.isPending}
            showDeleteReason={showDeleteReason}
            toggleDeleteReason={() => setShowDeleteReason(!showDeleteReason)}
          />
        )}

        <PinVerificationModal
          isOpen={verifyModal}
          toggle={() => setVerifyModal(false)}
          onVerify={handlePinVerify}
          isLoading={verifyPinMutation.isPending}
          title="Verify Transfer Record"
          description={
            <>
              Please enter your password to verify Record:
              <br />
              <strong>{selectedRecord?.transfernumber}</strong>
            </>
          }
          isInvalid={verifyPinMutation.isError}
        />
      </Container>
    </div>
  );
};

export default TransferList;
